import type { VolumeTargets } from '@cadencia/contracts';
import type { Exercise } from '../catalog/exercise-model.js';
import { ORIGINAL_CATALOG_SLUGS } from '../catalog/exercises.js';
import { MUSCLES, type Muscle } from '../catalog/muscles.js';
import { SAFETY_LIMITS } from '../safety/limits.js';
import { bandFor, type VolumeBand } from '../training/volume.js';

/**
 * Propuesta de estructura de bloque: que se acepta de ella y con que limites
 * (docs/AI_ARCHITECTURE.md §2 y §8, pasos 3 y 4).
 *
 * POR QUE AQUI SE RECORTA Y EN `exercise-selection.ts` SE RECHAZA ENTERO
 *
 * Es la misma cadena del §8 con dos formas distintas, y la diferencia no es de
 * criterio: es de la NATURALEZA de lo que se propone.
 *
 *   - Una sesion es un conjunto. Los huecos se eligen unos en funcion de otros,
 *     asi que aceptar la mitad da una sesion que no ha disenado nadie.
 *   - Un bloque es una lista de numeros independientes. Que el pectoral lleve
 *     16 series no depende de que el gemelo lleve 8. Recortar el que se pasa y
 *     dejar los demas produce exactamente lo que el §8 paso 4 describe:
 *     «`safety/limits.ts` recorta o descarta».
 *
 * Rechazar el bloque entero porque un musculo se paso dos series tiraria
 * diecinueve decisiones buenas por una discutible.
 *
 * QUE SE PUEDE PROPONER Y QUE NO
 *
 * Semanas y series por musculo. **El split no**: hoy solo dos splits tienen
 * plantillas y las dos apuntan a las mismas sesiones, asi que elegirlo no
 * cambiaria nada, y una eleccion sin efecto enseñada como si lo tuviera es peor
 * que ninguna. La fase tampoco: la decide `selectBlockPhase` con las señales
 * del §2.3, y es de las que no se negocian.
 */

/** Cuanto puede acortarse o alargarse un bloque respecto a lo tabulado (§2.3). */
export const BLOCK_WEEKS_SLACK = { shorter: 3, longer: 2 } as const;

export interface BlockProposalInput {
  /** Semanas que la fase tiene tabuladas. El punto de partida. */
  defaultWeeks: number;
  /**
   * Series por musculo que produce la plantilla, la primera semana.
   *
   * Es la referencia del limite del ±20 %: sin una base, «cambio semanal» no
   * significa nada y el tope no se podria aplicar.
   */
  baseline: Readonly<Partial<Record<Muscle, number>>>;
  proposal: {
    weeks: number;
    volumeTargets: readonly { muscle: string; sets: number }[];
  };
  /** Bandas por musculo, con sus posibles ajustes del usuario. */
  bandOverrides?: Partial<Record<Muscle, VolumeBand>> | undefined;
}

export interface BlockProposalVerdict {
  weeks: number;
  targets: VolumeTargets;
  /**
   * Lo que se toco y por que, en vocabulario del dominio. Se registra y se
   * puede enseñar: un recorte silencioso convertiria «la IA propuso 26 series»
   * en «la IA propuso 22» sin que nadie pudiera saberlo.
   */
  adjustments: string[];
  /** Entradas que no se pudieron interpretar y se cayeron enteras. */
  dropped: string[];
}

const MUSCULOS = new Set<string>(MUSCLES);

function acotar(valor: number, min: number, max: number): number {
  return Math.min(Math.max(valor, min), max);
}

export function validateBlockProposal(input: BlockProposalInput): BlockProposalVerdict {
  const adjustments: string[] = [];
  const dropped: string[] = [];

  // --- semanas ------------------------------------------------------------
  const minimo = Math.max(1, input.defaultWeeks - BLOCK_WEEKS_SLACK.shorter);
  const maximo = input.defaultWeeks + BLOCK_WEEKS_SLACK.longer;
  const weeks = acotar(Math.trunc(input.proposal.weeks), minimo, maximo);
  if (weeks !== input.proposal.weeks) {
    adjustments.push(`semanas ${input.proposal.weeks} → ${weeks} (rango ${minimo}-${maximo})`);
  }

  // --- volumen por musculo ------------------------------------------------
  const targets: Partial<Record<Muscle, number>> = {};

  for (const entrada of input.proposal.volumeTargets) {
    if (!MUSCULOS.has(entrada.muscle)) {
      // Un musculo inventado no se recorta: no hay nada a lo que acercarlo.
      dropped.push(`músculo desconocido: ${entrada.muscle}`);
      continue;
    }
    const muscle = entrada.muscle as Muscle;
    if (targets[muscle] !== undefined) {
      dropped.push(`músculo repetido: ${entrada.muscle}`);
      continue;
    }

    const band = bandFor(muscle, input.bandOverrides ?? {});
    let sets = acotar(Math.round(entrada.sets), band.min, band.max);
    if (sets !== entrada.sets) {
      adjustments.push(`${muscle}: ${entrada.sets} → ${sets} (banda ${band.min}-${band.max})`);
    }

    // Y despues del ±20 % sobre lo que da la plantilla. Va DESPUES de la banda
    // a proposito: la banda dice que es sensato para ese musculo y el tope dice
    // cuanto se puede mover de golpe. Aplicar el tope primero dejaria pasar un
    // valor dentro del salto pero fuera de la banda.
    const base = input.baseline[muscle];
    if (base !== undefined && base > 0) {
      const margen = SAFETY_LIMITS.volume.maxWeeklyChangePct;
      const acotado = Math.round(acotar(sets, base * (1 - margen), base * (1 + margen)));
      if (acotado !== sets) {
        adjustments.push(
          `${muscle}: ${sets} → ${acotado} (máximo ${Math.round(margen * 100)} % sobre ${base})`,
        );
        sets = acotado;
      }
    }

    targets[muscle] = sets;
  }

  return { weeks, targets, adjustments, dropped };
}

/**
 * El catalogo que se ofrece a la IA en `block_proposal`: los ejercicios del
 * catalogo ORIGINAL que recibe, agrupados por sus musculos principales.
 *
 * LA BIBLIOTECA V2 NO VIAJA, Y NO ES UN DESCUIDO
 *
 * Con la biblioteca del 28/09/2026 el catalogo paso de 89 ejercicios a ~360.
 * Medido con `estimateContextTokens` en el peor caso de CA-7.6 (seis fichas
 * largas, seis recuerdos, gimnasio completo): el catalogo original cuesta
 * 1.088 tokens y todo lo demas ~2.720, o sea ~190 de margen contra el tope de
 * 4.000. El catalogo entero lo llevaba a 7.531, y aun recortado a 8 ejercicios
 * por grupo a 4.931. Pasado el tope, `coachAI` corta la peticion, el dominio
 * tira por su camino determinista y la pantalla no dice nada.
 *
 * Como con las recetas, se recorta lo que se envia y no el tope. La biblioteca
 * sigue en `EXERCISES`: la usan las sustituciones (`findSubstitutions`) y la
 * alternativa por limitaciones (`pickAlternativeForLimitations`), que no pasan
 * por la IA.
 *
 * Quien llama filtra antes lo que no se puede hacer (equipamiento,
 * limitaciones); aqui solo se restringe al catalogo original y se agrupa.
 */
export function catalogForBlockProposal(exercises: readonly Exercise[]): Record<string, string[]> {
  const catalogo: Record<string, string[]> = {};
  for (const exercise of exercises) {
    if (!ORIGINAL_CATALOG_SLUGS.has(exercise.slug)) continue;
    (catalogo[exercise.primaryMuscles.join('+')] ??= []).push(exercise.slug);
  }
  return catalogo;
}
